import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { checkSubscriptionStatus } from '../utils/auth';

const MemberContext = createContext(null);

// Max time (ms) to trust localStorage without a successful server verification
const MAX_TRUST_DURATION = 60 * 60 * 1000; // 1 hour
// How often to re-verify while the app is open
const REVERIFY_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useMember = () => {
  const ctx = useContext(MemberContext);
  if (!ctx) throw new Error('useMember must be used within a MemberProvider');
  return ctx;
};

export const MemberProvider = ({ children }) => {
  const [isMember, setIsMember] = useState(false);
  const [plan, setPlan] = useState(null); // e.g. 'freedom'
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const revokeAccess = useCallback(() => {
    console.log('🚫 Revoking member access');
    localStorage.setItem('memberStatus', 'inactive');
    localStorage.removeItem('memberPlan');
    localStorage.removeItem('lastVerified');
    setIsMember(false);
    setPlan(null);
    setSubscriptionStatus('cancelled');
  }, []);

  const verifySubscription = useCallback(async (isInitialLoad = false) => {
    try {
      const email = localStorage.getItem('userEmail');
      const localActive = localStorage.getItem('memberStatus') === 'active';

      if (!email || !localActive) {
        if (isInitialLoad) setIsLoading(false);
        return;
      }

      const status = await checkSubscriptionStatus(email);
      setSubscriptionStatus(status);

      if (status === 'cancelled' || status === 'inactive') {
        // Subscription was cancelled server-side — revoke local access immediately
        console.log('Subscription no longer active, revoking local access');
        revokeAccess();
      } else if (status === 'active' || status === 'past_due') {
        // Confirmed active — keep access and record verification time
        localStorage.setItem('lastVerified', Date.now().toString());
        setIsMember(true);
      } else {
        // status is null — member not found in DB or query failed
        // Check how long since last successful verification
        const lastVerified = parseInt(localStorage.getItem('lastVerified') || '0', 10);
        const elapsed = Date.now() - lastVerified;

        if (elapsed > MAX_TRUST_DURATION) {
          // Too long since last successful check — revoke access
          console.log('Cannot verify subscription & trust window expired, revoking access');
          revokeAccess();
        } else {
          // Still within grace window — keep access but warn
          console.warn('Could not verify subscription, within grace period');
        }
      }
    } catch (e) {
      // Network error — check grace period
      console.warn('Verification network error:', e);
      const lastVerified = parseInt(localStorage.getItem('lastVerified') || '0', 10);
      const elapsed = Date.now() - lastVerified;

      if (elapsed > MAX_TRUST_DURATION) {
        console.log('Network error & trust window expired, revoking access');
        revokeAccess();
      }
    } finally {
      if (isInitialLoad) setIsLoading(false);
    }
  }, [revokeAccess]);

  useEffect(() => {
    // Step 1: Quickly load from localStorage for instant UI
    try {
      const status = localStorage.getItem('memberStatus');
      const planId = localStorage.getItem('memberPlan');
      const localActive = status === 'active';
      setIsMember(localActive);
      setPlan(planId || null);
    } catch (e) {
      console.warn('Error reading member status', e);
    }

    // Step 2: Verify against Supabase immediately
    verifySubscription(true);

    // Step 3: Re-verify periodically while the app is open
    const interval = setInterval(() => {
      verifySubscription(false);
    }, REVERIFY_INTERVAL);

    // Step 4: Re-verify when the tab becomes visible again (user switches back)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        verifySubscription(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [verifySubscription]);

  const startMembership = ({ planId = 'freedom', waiverAccepted, termsAccepted, source } = {}) => {
    localStorage.setItem('memberStatus', 'active');
    localStorage.setItem('memberPlan', planId);
    localStorage.setItem('memberSince', new Date().toISOString());
    localStorage.setItem('lastVerified', Date.now().toString());
    const meta = {
      waiverAccepted: Boolean(waiverAccepted),
      termsAccepted: Boolean(termsAccepted),
      source: source || 'signup'
    };
    localStorage.setItem('memberMeta', JSON.stringify(meta));
    setIsMember(true);
    setPlan(planId);
  };

  const cancelMembership = () => {
    localStorage.setItem('memberStatus', 'inactive');
    localStorage.removeItem('memberPlan');
    localStorage.removeItem('memberMeta');
    setIsMember(false);
    setPlan(null);
  };

  const logout = () => {
    // Clear all member-related data
    localStorage.removeItem('memberStatus');
    localStorage.removeItem('memberPlan');
    localStorage.removeItem('memberMeta');
    localStorage.removeItem('memberSince');
    localStorage.removeItem('userEmail');
    setIsMember(false);
    setPlan(null);
  };

  return (
    <MemberContext.Provider value={{ isMember, plan, isLoading, subscriptionStatus, startMembership, cancelMembership, logout }}>
      {children}
    </MemberContext.Provider>
  );
};
